import React, { useState, useEffect } from 'react';
import { toISODateOrNull, formatDateForDateInput } from '../../utils/dateUtils';
import { userAPI, taskAPI } from '../../api/api';
import './JobForm.css';
import { onayla } from '../../utils/onayla';

const JobForm = ({ job, onSubmit, onCancel, onDelete, loading, onAssignedAsTask }) => {
  const [formData, setFormData] = useState({
    Description: '',
    DueDate: ''
  });
  const [showAssignTask, setShowAssignTask] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (job) {
      setFormData({
        Description: job.Description || job.description || '',
        DueDate: formatDateForDateInput(job.DueDate || job.dueDate)
      });
      setShowAssignTask(false);
      setSelectedUserId('');
    }
  }, [job]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.Description.trim()) {
      alert('İş açıklaması boş olamaz!');
      return;
    }

    // DueDate varsa ve sadece tarih formatındaysa (YYYY-MM-DD), saat 23:59 ekle
    let dueDateValue = formData.DueDate;
    if (dueDateValue && /^\d{4}-\d{2}-\d{2}$/.test(dueDateValue)) {
      dueDateValue = `${dueDateValue}T23:59`;
    }

    onSubmit({
      Description: formData.Description.trim(),
      DueDate: toISODateOrNull(dueDateValue)
    });
  };

  const openAssignTask = async () => {
    setShowAssignTask(true);
    if (users.length > 0) return;
    setLoadingUsers(true);
    try {
      const data = await userAPI.getAll();
      const list = (data || []).filter((u) => {
        const active = u.IsActive ?? u.isActive;
        return active !== false;
      });
      list.sort((a, b) => {
        const an = `${a.Name ?? a.name ?? ''} ${a.Surname ?? a.surname ?? ''}`.trim();
        const bn = `${b.Name ?? b.name ?? ''} ${b.Surname ?? b.surname ?? ''}`.trim();
        return an.localeCompare(bn, 'tr');
      });
      setUsers(list);
    } catch (err) {
      console.error('Kullanıcılar yüklenirken hata:', err);
      alert('Kullanıcı listesi yüklenemedi: ' + (err.response?.data?.message || err.message));
      setShowAssignTask(false);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAssignAsTask = async () => {
    if (!job) return;
    if (!selectedUserId) {
      alert('Lütfen bir kullanıcı seçin.');
      return;
    }

    const jobId = job.Id ?? job.id;
    const caseFileId = job.CaseFileId ?? job.caseFileId ?? null;
    let dueDateValue = formData.DueDate || formatDateForDateInput(job.DueDate || job.dueDate);
    if (dueDateValue && /^\d{4}-\d{2}-\d{2}$/.test(dueDateValue)) {
      dueDateValue = `${dueDateValue}T23:59`;
    }

    setAssigning(true);
    try {
      await taskAPI.create({
        CaseFileId: caseFileId || null,
        AssignedToUserId: parseInt(selectedUserId, 10),
        Description: (formData.Description || job.Description || job.description || '').trim(),
        DueDate: toISODateOrNull(dueDateValue),
        SourceJobId: jobId
      });
      alert('İş seçilen kullanıcıya görev olarak atandı. İş listesinde kalmaya devam eder; görev onaylanınca her ikisi de silinir.');
      setShowAssignTask(false);
      setSelectedUserId('');
      if (onAssignedAsTask) {
        await onAssignedAsTask();
      }
    } catch (err) {
      console.error('Görev atama hatası:', err);
      alert('Görev atanamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="job-form-overlay" onClick={(e) => {
      if (e.target.classList.contains('job-form-overlay')) {
        onCancel();
      }
    }}>
      <div className="job-form-container" onClick={(e) => e.stopPropagation()}>
        <div className="job-form-header">
          <h2>{job ? 'İşi Düzenle' : 'Yeni İş Oluştur'}</h2>
          <button className="btn btn-secondary" onClick={onCancel}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="job-form">
          <div className="form-group">
            <label>İş Açıklaması *</label>
            <textarea
              name="Description"
              value={formData.Description}
              onChange={handleChange}
              className="form-control"
              rows="6"
              placeholder="Yapılacak işin açıklaması"
              required
            />
          </div>

          <div className="form-group">
            <label>Son Tarih (Opsiyonel)</label>
            <input
              type="date"
              name="DueDate"
              value={formData.DueDate}
              onChange={handleChange}
              className="form-control"
            />
            <small className="form-text text-muted">
              İşin tamamlanması gereken son tarih.
            </small>
          </div>

          {job && (
            <div className="form-group job-assign-task-block">
              {!showAssignTask ? (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openAssignTask}
                  disabled={loading || assigning}
                >
                  Bu işi görev olarak ata
                </button>
              ) : (
                <div className="job-assign-task-panel">
                  <label>Görevi atayacağınız kullanıcı *</label>
                  {loadingUsers ? (
                    <p className="form-text text-muted">Kullanıcılar yükleniyor...</p>
                  ) : (
                    <select
                      className="form-control"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      <option value="">---------</option>
                      {users.map((u) => {
                        const uid = u.Id ?? u.id;
                        const name = `${u.Name ?? u.name ?? ''} ${u.Surname ?? u.surname ?? ''}`.trim()
                          || (u.Username ?? u.username ?? uid);
                        return (
                          <option key={uid} value={uid}>{name}</option>
                        );
                      })}
                    </select>
                  )}
                  <div className="job-assign-task-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setShowAssignTask(false); setSelectedUserId(''); }}
                      disabled={assigning}
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleAssignAsTask}
                      disabled={assigning || loadingUsers || !selectedUserId}
                    >
                      {assigning ? 'Atanıyor...' : 'Görevi Ata'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div>
                {job && onDelete && (
                  <button 
                    type="button" 
                    onClick={async () => {
                      if (await onayla('Bu işi silmek istediğinize emin misiniz?')) {
                        await onDelete();
                      }
                    }} 
                    className="btn btn-danger"
                    disabled={loading || assigning}
                  >
                    Sil
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={assigning}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading || assigning}>
                  {loading ? 'Kaydediliyor...' : (job ? 'Güncelle' : 'Oluştur')}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;
